// Pure helpers for the Marketplace panel: prices, install status, permission
// consent, the settings form, review eligibility, theme previews, the Stripe
// return marker and the plugin panel bridge. No React/store imports.
import { PERMISSIONS, PLUGIN_CATEGORIES, SKILL_CATEGORIES, TEMPLATE_CATEGORIES, THEME_FONTS } from '~/lib/plugin-sdk';
import type {
  InstallStatus,
  InstallView,
  ListingBadge,
  ListingCard,
  ListingDetail,
  ListingKind,
  PricingView,
  RemixResult,
  UsageStatement,
} from '@dunea/shared';
import type { Tone } from '~/components/workbench/tools/payments/payments-model';

export { apiErrorMessage } from '~/components/workbench/tools/payments/payments-model';
export { starStates } from '~/components/workbench/tools/reviews/reviews-model';

// ─── catalog ───

export type MarketplaceView = 'plugins' | 'themes' | 'templates' | 'skills' | 'experts' | 'wishlist' | 'installed';

export const MARKETPLACE_VIEWS = [
  ['plugins', 'Plugins'],
  ['themes', 'Themes'],
  ['templates', 'Templates'],
  ['skills', 'Skills'],
  ['experts', 'Experts'],
  ['wishlist', 'Wishlist'],
  ['installed', 'Installed'],
] as const satisfies readonly (readonly [MarketplaceView, string])[];

/** The catalog kind and categories behind a browse tab (themes have their own view). */
export const CATALOG_TABS = {
  plugins: { kind: 'plugin', noun: 'plugins', categories: PLUGIN_CATEGORIES },
  templates: { kind: 'template', noun: 'templates', categories: TEMPLATE_CATEGORIES },
  skills: { kind: 'skill', noun: 'skills', categories: SKILL_CATEGORIES },
} as const satisfies Record<string, { kind: ListingKind; noun: string; categories: readonly string[] }>;

export type CatalogTab = keyof typeof CATALOG_TABS;

const KIND_NOUN: Record<ListingKind, string> = {
  plugin: 'plugin',
  theme: 'theme',
  template: 'template',
  skill: 'skill',
};

export function kindNoun(kind: string): string {
  return KIND_NOUN[kind as ListingKind] ?? 'listing';
}

export function kindLabel(kind: string): string {
  const noun = kindNoun(kind);
  return noun[0].toUpperCase() + noun.slice(1);
}

// ─── badges ───

const BADGE_META: Record<ListingBadge, { label: string; tone: Tone; title: string }> = {
  'by-dunea': { label: 'By Dunea', tone: 'accent', title: 'Made by Dunea' },
  featured: { label: 'Featured', tone: 'info', title: 'Picked by Dunea' },
  verified: { label: 'Verified', tone: 'success', title: '10+ active installs, 3+ reviews, rated 4.3 or higher' },
  new: { label: 'New', tone: 'warning', title: 'Listed in the last 90 days' },
};

const BADGE_ORDER: ListingBadge[] = ['by-dunea', 'featured', 'verified', 'new'];

/** Known badges in a fixed order, de-duplicated; `featured` also comes from the flag. */
export function listingBadges(
  card: Pick<ListingCard, 'badges' | 'featured'>,
): { id: ListingBadge; label: string; tone: Tone; title: string }[] {
  const have = new Set<string>(card.badges ?? []);

  if (card.featured) {
    have.add('featured');
  }

  return BADGE_ORDER.filter((b) => have.has(b)).map((id) => ({ id, ...BADGE_META[id] }));
}

/** "$9/mo + usage", "Free + usage", or the plain price. */
export function cardPriceLabel(card: Pick<ListingCard, 'pricing' | 'metered'>): string {
  const base = priceLabel(card.pricing);
  return card.metered ? `${base} + usage` : base;
}

export const SORTS = [
  ['popular', 'Popular'],
  ['rating', 'Top rated'],
  ['new', 'New'],
] as const;

/** "customer-support" → "Customer support", "ai" → "AI". */
export function categoryLabel(category: string): string {
  if (category === 'ai') {
    return 'AI';
  }

  const words = category.replace(/[-_]+/g, ' ').trim();

  return words ? words[0].toUpperCase() + words.slice(1) : category;
}

/** USD cents → "$9", "$9.50", "$1,200". */
export function formatUsd(cents: number): string {
  const dollars = (Number.isFinite(cents) ? cents : 0) / 100;
  const whole = Number.isInteger(dollars);

  return `$${dollars.toLocaleString('en-US', {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

/** "Free", "$9 one-time", "$5/mo · 7-day trial". */
export function priceLabel(pricing: PricingView | null | undefined): string {
  if (!pricing || pricing.model === 'free' || !pricing.amount) {
    return 'Free';
  }

  if (pricing.model === 'one_time') {
    return `${formatUsd(pricing.amount)} one-time`;
  }

  const trial = pricing.trialDays && pricing.trialDays > 0 ? ` · ${pricing.trialDays}-day trial` : '';

  return `${formatUsd(pricing.amount)}/mo${trial}`;
}

export function isPaid(pricing: PricingView | null | undefined): boolean {
  return Boolean(pricing && pricing.model !== 'free' && pricing.amount);
}

/** 950 → "950", 1200 → "1.2k", 2_400_000 → "2.4M". */
export function formatCount(n: number): string {
  if (!Number.isFinite(n) || n < 1000) {
    return String(Math.max(0, Math.round(n || 0)));
  }

  const [value, suffix] = n >= 1_000_000 ? [n / 1_000_000, 'M'] : [n / 1000, 'k'];
  const rounded = value >= 100 ? Math.round(value) : Math.round(value * 10) / 10;

  return `${rounded}${suffix}`;
}

export function installsLabel(n: number): string {
  return n === 1 ? '1 install' : `${formatCount(n)} installs`;
}

export function ratingSummary(rating: number, count: number): string {
  if (!count) {
    return 'No ratings yet';
  }

  return `${(Math.round(rating * 10) / 10).toFixed(1)} · ${count === 1 ? '1 rating' : `${formatCount(count)} ratings`}`;
}

// ─── install status ───

const STATUS_META: Record<InstallStatus, { label: string; tone: Tone }> = {
  active: { label: 'Active', tone: 'success' },
  pending_payment: { label: 'Pending payment', tone: 'warning' },
  suspended: { label: 'Suspended', tone: 'danger' },
  uninstalled: { label: 'Uninstalled', tone: 'muted' },
};

export function installStatusMeta(status: string): { label: string; tone: Tone } {
  return STATUS_META[status as InstallStatus] ?? { label: status, tone: 'muted' };
}

/** Owner-facing line for a plugin whose checkout functions fell back today. */
export function functionFailuresLabel(n: number): string {
  const times = n === 1 ? 'once' : `${formatCount(n)} times`;

  return `Its checkout logic didn't answer ${times} today. Checkout kept working with your shop's own settings.`;
}

/** The owner's current install of a listing (uninstalled rows don't count). */
export function installFor(installs: readonly InstallView[] | null | undefined, slug: string): InstallView | null {
  return installs?.find((i) => i.slug === slug && i.status !== 'uninstalled') ?? null;
}

// ─── permissions + consent ───

export function permissionLabel(id: string, fallback?: string): string {
  return (PERMISSIONS as Record<string, string>)[id] ?? fallback ?? id;
}

/** Labels for what a listing requests; the API's label wins, the SDK's is the fallback. */
export function requestedPermissions(
  permissions: readonly { id: string; label?: string | null }[] | null | undefined,
): { id: string; label: string }[] {
  return (permissions ?? []).map((p) => ({ id: p.id, label: p.label || permissionLabel(p.id) }));
}

/** Granted + pending, de-duplicated — the full list a "Review new permissions" PATCH sends. */
export function permissionsAfterApproval(install: Pick<InstallView, 'grantedPermissions' | 'pendingPermissions'>) {
  return Array.from(new Set([...install.grantedPermissions, ...install.pendingPermissions]));
}

export interface InstallReadiness {
  ready: boolean;
  /** Why Install is disabled, for a hint under the button. */
  reason: string | null;
}

/** Install needs consent (when anything is requested) and a valid settings form. */
export function installReadiness(input: {
  permissionCount: number;
  consented: boolean;
  settingsErrors: Record<string, string>;
}): InstallReadiness {
  if (input.permissionCount > 0 && !input.consented) {
    return { ready: false, reason: 'Allow the permissions above to install.' };
  }

  if (Object.keys(input.settingsErrors).length > 0) {
    return { ready: false, reason: 'Fill in the required settings.' };
  }

  return { ready: true, reason: null };
}

export function consentSentence(name: string): string {
  return `I allow ${name} to do the things listed above.`;
}

// ─── settings form ───

export type SettingsFieldType = 'text' | 'secret' | 'select' | 'boolean' | 'number' | 'email' | 'url';

export interface SettingsField {
  key: string;
  label: string;
  type: SettingsFieldType;
  help?: string;
  placeholder?: string;
  required?: boolean;
  options?: { value: string; label: string }[];
}

const FIELD_TYPES = new Set<SettingsFieldType>(['text', 'secret', 'select', 'boolean', 'number', 'email', 'url']);

/**
 * The manifest's settings fields. NOT in the frozen ListingDetail contract, so
 * this reads an optional `settings` array if the API sends one and drops
 * anything malformed.
 */
export function listingSettingsFields(detail: ListingDetail | null | undefined): SettingsField[] {
  const raw = (detail as { settings?: unknown } | null | undefined)?.settings;

  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.filter(
    (f): f is SettingsField =>
      Boolean(f) &&
      typeof f === 'object' &&
      typeof (f as SettingsField).key === 'string' &&
      typeof (f as SettingsField).label === 'string' &&
      FIELD_TYPES.has((f as SettingsField).type),
  );
}

export type SettingsValues = Record<string, string | boolean>;

/** Form state from saved settings. Secrets always start empty (write-only). */
export function initialSettingsValues(
  fields: readonly SettingsField[],
  current: Record<string, unknown> = {},
): SettingsValues {
  const values: SettingsValues = {};

  for (const f of fields) {
    const saved = current[f.key];

    if (f.type === 'boolean') {
      values[f.key] = saved === true;
    } else if (f.type === 'secret') {
      values[f.key] = '';
    } else {
      values[f.key] = saved === undefined || saved === null ? '' : String(saved);
    }
  }

  return values;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Per-field errors. A required secret already on file may stay blank. */
export function settingsErrors(
  fields: readonly SettingsField[],
  values: SettingsValues,
  secretsSet: readonly string[] = [],
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const f of fields) {
    if (f.type === 'boolean') {
      continue;
    }

    const value = String(values[f.key] ?? '').trim();

    if (!value) {
      if (f.required && !(f.type === 'secret' && secretsSet.includes(f.key))) {
        errors[f.key] = `${f.label} is required.`;
      }

      continue;
    }

    if (f.type === 'email' && !EMAIL.test(value)) {
      errors[f.key] = 'Enter a valid email address.';
    } else if (f.type === 'url' && !/^https?:\/\/\S+$/i.test(value)) {
      errors[f.key] = 'Enter a full URL starting with https://';
    } else if (f.type === 'number' && !Number.isFinite(Number(value))) {
      errors[f.key] = 'Enter a number.';
    } else if (f.type === 'select' && f.options && !f.options.some((o) => o.value === value)) {
      errors[f.key] = 'Pick one of the options.';
    }
  }

  return errors;
}

/** What to send: typed values, and secrets only when the owner typed a new one. */
export function settingsPayload(fields: readonly SettingsField[], values: SettingsValues): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  for (const f of fields) {
    const value = values[f.key];

    if (f.type === 'boolean') {
      out[f.key] = value === true;
      continue;
    }

    const text = String(value ?? '').trim();

    if (f.type === 'secret') {
      if (text) {
        out[f.key] = text;
      }

      continue;
    }

    if (f.type === 'number') {
      out[f.key] = text ? Number(text) : null;
      continue;
    }

    out[f.key] = text || null;
  }

  return out;
}

// ─── reviews ───

export interface ReviewEligibility {
  canReview: boolean;
  /** Shown instead of the "Write a review" button. */
  message: string | null;
}

/** Only an owner who has installed the listing on this app may review it. */
export function reviewEligibility(install: InstallView | null | undefined, kind: ListingKind): ReviewEligibility {
  const noun = kindNoun(kind);

  if (!install || install.status === 'uninstalled') {
    return {
      canReview: false,
      message:
        kind === 'template'
          ? 'Start a site from this template to review it.'
          : `Install this ${noun} to write a review.`,
    };
  }

  if (install.status === 'pending_payment') {
    return { canReview: false, message: 'Finish checkout to write a review.' };
  }

  return { canReview: true, message: null };
}

export const REVIEW_TITLE_MAX = 120;
export const REVIEW_BODY_MAX = 2000;

export function reviewDraftError(draft: { rating: number; title: string; body: string }): string | null {
  if (!Number.isInteger(draft.rating) || draft.rating < 1 || draft.rating > 5) {
    return 'Pick a rating from 1 to 5 stars.';
  }

  if (draft.title.length > REVIEW_TITLE_MAX) {
    return `Keep the title under ${REVIEW_TITLE_MAX} characters.`;
  }

  if (draft.body.length > REVIEW_BODY_MAX) {
    return `Keep the review under ${REVIEW_BODY_MAX} characters.`;
  }

  return null;
}

// ─── credits: paid tools, usage, spend cap ───

/** 1200 → "1,200", 0.5 → "0.5" (two decimals at most). */
export function creditsNumber(n: number): string {
  const value = Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

/** 0.5 → "0.5 credits", 1 → "1 credit", 1200 → "1,200 credits". */
export function creditsLabel(n: number): string {
  const text = creditsNumber(n);
  return `${text} ${text === '1' ? 'credit' : 'credits'}`;
}

export const DEFAULT_SPEND_CAP = 50;
export const SPEND_CAP_MAX = 100_000;

export const SPEND_CAP_SENTENCE = "Paid tools and usage stop when this month's cap is reached.";

type ChargeShape = Pick<ListingDetail, 'kind' | 'usage'> & {
  capabilities?: Pick<ListingDetail['capabilities'], 'agentTools'> | null;
};

/** Plugins with a priced AI tool or metered usage need a monthly cap at install. */
export function needsSpendCap(detail: ChargeShape | null | undefined): boolean {
  if (!detail || detail.kind !== 'plugin') {
    return false;
  }

  const pricedTool = (detail.capabilities?.agentTools ?? []).some(
    (t) => typeof t.priceCredits === 'number' && t.priceCredits > 0,
  );

  return pricedTool || Boolean(detail.usage);
}

/** The cap to pre-fill: the developer's suggestion, else 50. */
export function defaultSpendCap(detail: Pick<ListingDetail, 'usage'> | null | undefined): number {
  const suggested = detail?.usage?.suggestedMonthlyCapCredits;
  return typeof suggested === 'number' && Number.isFinite(suggested) && suggested > 0 ? suggested : DEFAULT_SPEND_CAP;
}

/** Parse the cap input: a whole number of credits, 1 to SPEND_CAP_MAX. */
export function parseSpendCap(input: string): { value: number | null; error: string | null } {
  const text = input.trim().replace(/,/g, '');

  if (!text) {
    return { value: null, error: 'Set a monthly cap.' };
  }

  const n = Number(text);

  if (!Number.isFinite(n) || !Number.isInteger(n)) {
    return { value: null, error: 'Enter a whole number of credits.' };
  }

  if (n < 1 || n > SPEND_CAP_MAX) {
    return { value: null, error: `Between 1 and ${SPEND_CAP_MAX.toLocaleString('en-US')} credits.` };
  }

  return { value: n, error: null };
}

/** Spent vs cap for the installed row: "12 of 50 credits this month", plus a 0–1 fill. */
export function spendSummary(install: Pick<InstallView, 'spendCapCredits' | 'spentThisMonthCredits'>): {
  text: string;
  ratio: number;
  reached: boolean;
} {
  const spent = Math.max(0, install.spentThisMonthCredits || 0);
  const cap = install.spendCapCredits;

  if (cap === null || cap === undefined) {
    return { text: 'Paid tools and usage are off', ratio: 0, reached: false };
  }

  const ratio = cap > 0 ? Math.min(1, spent / cap) : 1;

  return { text: `${creditsNumber(spent)} of ${creditsLabel(cap)} this month`, ratio, reached: spent >= cap };
}

/** "2026-11" for a date (UTC). */
export function monthKey(date = new Date()): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** This install's slice of the month's statement. */
export function usageFor(statement: UsageStatement | null | undefined, installId: string) {
  return statement?.byInstall.find((r) => r.installId === installId) ?? null;
}

// ─── templates ───

export type RemixOutcome =
  | { kind: 'redirect'; url: string }
  | { kind: 'open'; chatId: string; path: string; firstPrompt: string | null; recommendedPlugins: string[] }
  | { kind: 'error'; message: string };

/** What to do with a remix response: leave for Checkout, or open the new chat. */
export function remixOutcome(result: RemixResult | null | undefined): RemixOutcome {
  if (
    result?.status === 'pending_payment' &&
    typeof result.checkoutUrl === 'string' &&
    /^https:\/\//.test(result.checkoutUrl)
  ) {
    return { kind: 'redirect', url: result.checkoutUrl };
  }

  if (result?.status === 'created' && typeof result.chatId === 'string' && result.chatId) {
    const prompt =
      typeof result.firstPrompt === 'string' && result.firstPrompt.trim() ? result.firstPrompt.trim() : null;

    return {
      kind: 'open',
      chatId: result.chatId,
      path: `/chat/${encodeURIComponent(result.chatId)}`,
      firstPrompt: prompt,
      recommendedPlugins: Array.isArray(result.recommendedPlugins) ? result.recommendedPlugins : [],
    };
  }

  return { kind: 'error', message: 'Could not start from this template.' };
}

/** "Used 1 time", "Used 1.2k times". */
export function remixLabel(count: number): string {
  return count === 1 ? 'Used 1 time' : `Used ${formatCount(count)} times`;
}

/** Only https preview links are rendered. */
export function safeHttpsUrl(url: string | null | undefined): string | null {
  if (!url) {
    return null;
  }

  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' ? parsed.toString() : null;
  } catch {
    return null;
  }
}

// ─── checkout return marker ───

const RETURN_PARAM = 'marketplace';
const INSTALL_PARAM = 'install';

export interface MarketplaceReturn {
  outcome: 'success' | 'cancel';
  installId: string | null;
}

/** `?marketplace=success&install=<id>` (or `=cancel`) after Stripe Checkout. */
export function marketplaceReturn(search: string): MarketplaceReturn | null {
  const params = new URLSearchParams(search);
  const value = params.get(RETURN_PARAM);
  const outcome = value === 'success' ? 'success' : value === 'cancel' || value === 'cancelled' ? 'cancel' : null;

  return outcome ? { outcome, installId: params.get(INSTALL_PARAM) } : null;
}

/** The current URL without the checkout outcome (and its panel marker), for history.replaceState. */
export function withoutMarketplaceParams(href: string): string {
  const url = new URL(href);
  url.searchParams.delete(RETURN_PARAM);
  url.searchParams.delete(INSTALL_PARAM);

  if (url.searchParams.get('panel') === 'marketplace') {
    url.searchParams.delete('panel');
  }

  return url.toString();
}

/** Where checkout sends the owner back: this page, marked to reopen the Marketplace panel. */
export function installReturnUrl(href: string): string {
  const url = new URL(withoutMarketplaceParams(href));
  url.searchParams.set('panel', 'marketplace');

  return url.toString();
}

// ─── themes ───

export interface PreviewTokens {
  colors: {
    background: string;
    surface: string;
    text: string;
    muted: string;
    primary: string;
    primaryText: string;
    accent: string;
    border: string;
  };
  fonts: { heading: string; body: string; mono?: string };
  radius: number;
  density: 'compact' | 'comfortable' | 'airy';
  typeScale: number;
}

const DEFAULT_TOKENS: PreviewTokens = {
  colors: {
    background: '#ffffff',
    surface: '#f5f5f4',
    text: '#111111',
    muted: '#6b6b6b',
    primary: '#111111',
    primaryText: '#ffffff',
    accent: '#e5e5e5',
    border: '#e2e2e2',
  },
  fonts: { heading: 'Inter', body: 'Inter' },
  radius: 8,
  density: 'comfortable',
  typeScale: 1.25,
};

const HEX = /^#[0-9a-fA-F]{6}$/;
const FONT_SET = new Set<string>(THEME_FONTS);

function hexOr(value: unknown, fallback: string): string {
  return typeof value === 'string' && HEX.test(value) ? value : fallback;
}

/** Only THEME_FONTS families are ever previewed or loaded. */
export function allowedFont(value: unknown, fallback = 'Inter'): string {
  return typeof value === 'string' && FONT_SET.has(value) ? value : fallback;
}

/** Tolerant read of theme tokens (the detail API types them as Record<string, unknown>). */
export function coerceThemeTokens(raw: unknown): PreviewTokens {
  const t = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const c = (t.colors && typeof t.colors === 'object' ? t.colors : {}) as Record<string, unknown>;
  const f = (t.fonts && typeof t.fonts === 'object' ? t.fonts : {}) as Record<string, unknown>;
  const d = DEFAULT_TOKENS;
  const radius =
    typeof t.radius === 'number' && Number.isFinite(t.radius) ? Math.min(32, Math.max(0, t.radius)) : d.radius;
  const typeScale =
    typeof t.typeScale === 'number' && Number.isFinite(t.typeScale)
      ? Math.min(1.6, Math.max(1.1, t.typeScale))
      : d.typeScale;
  const density = t.density === 'compact' || t.density === 'airy' ? t.density : 'comfortable';

  return {
    colors: {
      background: hexOr(c.background, d.colors.background),
      surface: hexOr(c.surface, d.colors.surface),
      text: hexOr(c.text, d.colors.text),
      muted: hexOr(c.muted, d.colors.muted),
      primary: hexOr(c.primary, d.colors.primary),
      primaryText: hexOr(c.primaryText, d.colors.primaryText),
      accent: hexOr(c.accent, d.colors.accent),
      border: hexOr(c.border, d.colors.border),
    },
    fonts: {
      heading: allowedFont(f.heading),
      body: allowedFont(f.body),
      ...(f.mono ? { mono: allowedFont(f.mono, 'JetBrains Mono') } : {}),
    },
    radius,
    density,
    typeScale,
  };
}

const SERIF = new Set([
  'Instrument Serif',
  'Playfair Display',
  'Cormorant Garamond',
  'Fraunces',
  'DM Serif Display',
  'Libre Baskerville',
  'Lora',
  'Merriweather',
  'EB Garamond',
]);
const MONO = new Set(['JetBrains Mono', 'Space Mono', 'Geist Mono']);

export function fontStack(family: string): string {
  const generic = SERIF.has(family) ? 'serif' : MONO.has(family) ? 'monospace' : 'sans-serif';
  return `'${family}', ${generic}`;
}

const DENSITY_GAP: Record<PreviewTokens['density'], string> = { compact: '8px', comfortable: '12px', airy: '18px' };

/** Inline CSS custom properties for the mini mock page. */
export function themePreviewVars(tokens: PreviewTokens): Record<string, string> {
  const { colors, fonts } = tokens;

  return {
    '--t-bg': colors.background,
    '--t-surface': colors.surface,
    '--t-text': colors.text,
    '--t-muted': colors.muted,
    '--t-primary': colors.primary,
    '--t-primary-text': colors.primaryText,
    '--t-accent': colors.accent,
    '--t-border': colors.border,
    '--t-radius': `${tokens.radius}px`,
    '--t-font-heading': fontStack(fonts.heading),
    '--t-font-body': fontStack(fonts.body),
    '--t-gap': DENSITY_GAP[tokens.density],
    '--t-h1': `${(11 * tokens.typeScale * tokens.typeScale).toFixed(1)}px`,
    '--t-h2': `${(11 * tokens.typeScale).toFixed(1)}px`,
  };
}

export function themeFontFamilies(tokens: PreviewTokens): string[] {
  return Array.from(new Set([tokens.fonts.heading, tokens.fonts.body]));
}

/** Families published in one weight only (asking Google for 700 would fail the whole request). */
const SINGLE_WEIGHT = new Set(['Instrument Serif', 'DM Serif Display']);

/** One Google Fonts css2 URL for these families (THEME_FONTS only), or null. */
export function googleFontsHref(families: readonly string[]): string | null {
  const allowed = Array.from(new Set(families.filter((f) => FONT_SET.has(f)))).sort();

  if (allowed.length === 0) {
    return null;
  }

  const parts = allowed.map((f) => {
    const name = f.replace(/ /g, '+');
    return SINGLE_WEIGHT.has(f) ? `family=${name}` : `family=${name}:wght@400;600;700`;
  });

  return `https://fonts.googleapis.com/css2?${parts.join('&')}&display=swap`;
}

export interface ThemeEntry {
  /** Theme id the AI's apply_theme takes (the listing slug for marketplace themes). */
  id: string;
  name: string;
  tagline: string;
  categories: string[];
  tokens: PreviewTokens;
  firstParty: boolean;
}

/**
 * `GET /api/marketplace/themes/first-party` returns theme manifests; accept a
 * bare array or `{ items | themes }` and drop entries without an id or name.
 */
export function normalizeFirstPartyThemes(data: unknown): ThemeEntry[] {
  const list = Array.isArray(data)
    ? data
    : data && typeof data === 'object'
      ? ((data as { items?: unknown; themes?: unknown }).items ?? (data as { themes?: unknown }).themes)
      : null;

  if (!Array.isArray(list)) {
    return [];
  }

  const out: ThemeEntry[] = [];

  for (const item of list) {
    if (!item || typeof item !== 'object') {
      continue;
    }

    const m = item as Record<string, unknown>;
    const id = typeof m.id === 'string' ? m.id : typeof m.slug === 'string' ? m.slug : null;
    const name = typeof m.name === 'string' ? m.name : null;

    if (!id || !name) {
      continue;
    }

    out.push({
      id,
      name,
      tagline: typeof m.tagline === 'string' ? m.tagline : '',
      categories: Array.isArray(m.categories) ? m.categories.filter((c): c is string => typeof c === 'string') : [],
      tokens: coerceThemeTokens(m.tokens ?? (m.theme as { tokens?: unknown } | undefined)?.tokens),
      firstParty: true,
    });
  }

  return out;
}

/** The chat message that asks the AI to apply a theme. */
export function applyThemePrompt(name: string, id: string): string {
  return `Apply the "${name}" theme (theme id: ${id}) to this app using apply_theme`;
}

// ─── plugin panel bridge ───

export const PANEL_MESSAGE_SOURCE = 'dunea-plugin';
export const PANEL_MAX_HEIGHT = 4000;

const TOAST_MAX = 300;

/** Mirrors `PanelMessage` / `isPanelMessage` in @dunea/plugin-sdk/panel. */
export type PanelMessage =
  | { type: 'toast'; message: string; variant: 'info' | 'success' | 'error' }
  | { type: 'navigate'; to: string }
  | { type: 'resize'; height: number }
  | { type: 'close' };

/** Validate (and sanitise) a postMessage payload. The caller must ALSO check event.source. */
export function parsePanelMessage(data: unknown): PanelMessage | null {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const m = data as Record<string, unknown>;

  if (m.source !== PANEL_MESSAGE_SOURCE) {
    return null;
  }

  switch (m.type) {
    case 'toast':
      return typeof m.message === 'string' && (m.variant === 'info' || m.variant === 'success' || m.variant === 'error')
        ? { type: 'toast', message: m.message.slice(0, TOAST_MAX), variant: m.variant }
        : null;
    case 'navigate':
      return typeof m.to === 'string' && m.to.length > 0 && m.to.length <= 300 ? { type: 'navigate', to: m.to } : null;
    case 'resize':
      return typeof m.height === 'number' && Number.isFinite(m.height) && m.height > 0
        ? { type: 'resize', height: Math.min(PANEL_MAX_HEIGHT, Math.round(m.height)) }
        : null;
    case 'close':
      return { type: 'close' };
    default:
      return null;
  }
}

/**
 * The iframe src, or null when it must not be framed: only https, and never our
 * own origin (allow-scripts + allow-same-origin on our origin would let the
 * frame lift its own sandbox).
 */
export function panelFrameSrc(url: string, hostOrigin: string): string | null {
  try {
    const parsed = new URL(url);

    if (parsed.protocol !== 'https:' || parsed.origin === hostOrigin) {
      return null;
    }

    return parsed.toString();
  } catch {
    return null;
  }
}

/** A navigate target: a workbench tool id, or an app path (opens the preview). */
export function panelNavigateTarget(
  to: string,
  tools: readonly string[],
): { kind: 'tool'; tool: string } | { kind: 'path'; path: string } | null {
  if (tools.includes(to)) {
    return { kind: 'tool', tool: to };
  }

  if (to.startsWith('/') && !to.startsWith('//')) {
    return { kind: 'path', path: to };
  }

  return null;
}
